import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getZktPunches } from "../../services/kmtAttendance.service";
import { formatAttendanceDateTimeCompact } from "../../utils/attendanceTimeFormat";
import {
  fillMonthPunchDays,
  toIsoDate,
} from "../../utils/fillMonthPunchDays";
import { formatHoursAndMinutes, hoursToMinutes } from "../../utils/lateHours";
import {
  formatPunchLeaveCoverText,
  listPunchLeaves,
} from "../../utils/punchLeaveCover";
import {
  formatPunchPermitCoverText,
  listPunchPermits,
} from "../../utils/punchPermitCover";
import { canHrConvertAttendanceToPermit } from "../../utils/employeeHistoryHelpers";
import { isHrFamilyRole } from "../../utils/hrStaffHelpers";
import PunchLocationLabel from "./PunchLocationLabel";
import ConvertAttendanceToPermitModal from "./ConvertAttendanceToPermitModal";
import MovePermitDateModal from "./MovePermitDateModal";
import PunchMonthTotals from "./PunchMonthTotals";
import "./EmployeePunchDaysModal.css";

function isUuid(value) {
  return (
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  );
}

const ARABIC_MONTHS = [
  { value: 1, label: "يناير" },
  { value: 2, label: "فبراير" },
  { value: 3, label: "مارس" },
  { value: 4, label: "أبريل" },
  { value: 5, label: "مايو" },
  { value: 6, label: "يونيو" },
  { value: 7, label: "يوليو" },
  { value: 8, label: "أغسطس" },
  { value: 9, label: "سبتمبر" },
  { value: 10, label: "أكتوبر" },
  { value: 11, label: "نوفمبر" },
  { value: 12, label: "ديسمبر" },
];

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function formatTimesheetShift(shift) {
  if (shift === "EVENING") return "مسائي";
  if (shift === "MORNING") return "صباحي";
  if (shift === "INVENTORY") return "جرد";
  return "";
}

function formatTimesheetDayType(dayType) {
  if (dayType === "WORK") return "عمل";
  if (dayType === "REST") return "راحة";
  if (dayType === "LEAVE") return "إجازة";
  if (dayType === "COVERAGE") return "تغطية";
  return "";
}

function TimesheetCompareUnderDay({ timesheet }) {
  if (!timesheet?.hasPlan) {
    return <div className="epdm-timesheet">لا يوجد تايم شيت</div>;
  }
  const window =
    timesheet.scheduledStartTime && timesheet.scheduledEndTime
      ? `${timesheet.scheduledStartTime}–${timesheet.scheduledEndTime}`
      : "";
  const plan = [
    formatTimesheetDayType(timesheet.dayType),
    formatTimesheetShift(timesheet.shift),
    window,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      className={`epdm-timesheet ${
        timesheet.aligned ? "is-match" : "is-mismatch"
      }`}
    >
      <div>التايم شيت: {plan || "—"}</div>
      <div>{timesheet.aligned ? "مطابق" : "غير مطابق"}</div>
    </div>
  );
}

function formatPunchSource(source) {
  if (source === "APP") return "تطبيق";
  if (source === "HR") return "يدوي HR";
  if (source === "ZKT" || source === "KMT") return "بصمة";
  return source || "—";
}

function formatAttendanceStatusBadge(status, lateMinutes) {
  const s = status || "";
  const minutes = Number(lateMinutes) || 0;
  switch (s) {
    case "PRESENT":
      return <span className="epdm-badge epdm-badge-success">حضور</span>;
    case "ABSENT":
      return <span className="epdm-badge epdm-badge-danger">غياب</span>;
    case "LATE":
      return (
        <span className="epdm-badge epdm-badge-warning" title={`${minutes} دقيقة تأخير`}>
          تأخير
        </span>
      );
    case "HALF_DAY":
      return <span className="epdm-badge epdm-badge-info">نصف يوم</span>;
    case "REST":
      return <span className="epdm-badge epdm-badge-primary">إجازة / راحة</span>;
    case "INCOMPLETE":
      return <span className="epdm-badge epdm-badge-muted">غير مكتمل</span>;
    default:
      return <span className="epdm-badge epdm-badge-muted">{s || "—"}</span>;
  }
}

function PunchPermitCoverCell({ permits }) {
  const list = listPunchPermits(permits);
  if (!list.length) return "—";
  return (
    <div className="epdm-permit-cover">
      {list.map((permit, index) => (
        <div key={`${permit.startTime}-${permit.endTime}-${index}`}>
          {formatPunchPermitCoverText([permit])}
        </div>
      ))}
    </div>
  );
}

function PunchLeaveCoverCell({ leaves }) {
  const list = listPunchLeaves(leaves);
  if (!list.length) return "—";
  return (
    <div className="epdm-permit-cover">
      {list.map((leave, index) => (
        <div key={`${leave.leaveType}-${leave.halfDayPeriod}-${index}`}>
          {formatPunchLeaveCoverText([leave])}
        </div>
      ))}
    </div>
  );
}

function shiftMonth(month, year, delta) {
  const next = new Date(Number(year), Number(month) - 1 + delta, 1);
  return { month: next.getMonth() + 1, year: next.getFullYear() };
}

function punchDayRowState(day, highlightIso) {
  const isHighlight = Boolean(highlightIso && toIsoDate(day.date) === highlightIso);
  const className = [
    day.incomplete ? "epdm-row-incomplete" : "",
    day.empty && day.status === "ABSENT" ? "epdm-row-absent" : "",
    day.empty && !day.status ? "epdm-row-empty" : "",
    isHighlight ? "epdm-row-highlight" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return { isHighlight, className };
}

function renderDayStatus(day) {
  if (day.empty && !day.status) {
    return (
      <span className="epdm-muted">{day.future ? "لم يأتِ بعد" : "بدون سجل"}</span>
    );
  }
  return formatAttendanceStatusBadge(day.status, day.lateMinutes);
}

function PunchDayCard({ day, highlightIso, onConvertPermit, onMovePermit }) {
  const { isHighlight, className } = punchDayRowState(day, highlightIso);
  const fields = [
    {
      label: "مكان البصمة",
      value: <PunchLocationLabel location={day.location} />,
    },
    {
      label: "البصمات",
      value: `${day.actualPunchCount ?? "—"} / ${day.expectedPunchCount ?? "—"}`,
    },
    {
      label: "الدخول",
      value: formatAttendanceDateTimeCompact(day.checkInTime),
      time: true,
    },
    {
      label: "الخروج",
      value: formatAttendanceDateTimeCompact(day.checkOutTime),
      time: true,
    },
    {
      label: "خروج البريك",
      value: formatAttendanceDateTimeCompact(day.breakOutTime),
      time: true,
    },
    {
      label: "عودة البريك",
      value: formatAttendanceDateTimeCompact(day.breakInTime),
      time: true,
    },
    {
      label: "الوقت",
      value: day.empty ? "—" : formatHoursAndMinutes(hoursToMinutes(day.totalWorkingHours)),
    },
    {
      label: "التأخير",
      value: day.empty ? "—" : formatHoursAndMinutes(day.lateMinutes),
    },
    {
      label: "انصراف مبكر",
      value: day.empty ? "—" : formatHoursAndMinutes(day.earlyMinutes),
    },
    {
      label: "الإجازة",
      value: <PunchLeaveCoverCell leaves={day.leaves} />,
    },
    {
      label: "الإذن",
      value: <PunchPermitCoverCell permits={day.permits} />,
    },
    {
      label: "المصدر",
      value: formatPunchSource(day.source),
    },
  ];

  return (
    <article className={`epdm-card ${className}`}>
      <header className="epdm-card-head">
        <div className="epdm-card-date-wrap">
          <div className="epdm-card-date">
            {day.date}
            {isHighlight ? <span className="epdm-incident-mark">يوم الواقعة</span> : null}
          </div>
          <TimesheetCompareUnderDay timesheet={day.timesheet} />
        </div>
        <div className="epdm-card-status">
          {renderDayStatus(day)}
          {onConvertPermit ? (
            <button
              type="button"
              className="epdm-convert-btn"
              onClick={() => onConvertPermit(day)}
            >
              تحويل لإذن
            </button>
          ) : null}
          {onMovePermit ? (
            <button
              type="button"
              className="epdm-convert-btn"
              onClick={() => onMovePermit(day)}
            >
              نقل الإذن
            </button>
          ) : null}
        </div>
      </header>
      <dl className="epdm-card-fields">
        {fields.map((field) => (
          <div key={field.label} className="epdm-card-field">
            <dt>{field.label}</dt>
            <dd className={field.time ? "punch-datetime" : undefined}>{field.value}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

export default function EmployeePunchDaysModal({
  employeeId,
  employeeName,
  employeeCode,
  month: initialMonth,
  year: initialYear,
  highlightDate,
  onClose,
}) {
  const [month, setMonth] = useState(Number(initialMonth));
  const [year, setYear] = useState(Number(initialYear));
  const { user } = useAuth();
  const [punchDays, setPunchDays] = useState([]);
  const [monthTotals, setMonthTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  const [convertDay, setConvertDay] = useState(null);
  const [movePermitDay, setMovePermitDay] = useState(null);
  const [convertSuccess, setConvertSuccess] = useState("");

  useEffect(() => {
    setMonth(Number(initialMonth));
    setYear(Number(initialYear));
  }, [employeeId, initialMonth, initialYear]);

  useEffect(() => {
    if (!employeeId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setPunchDays([]);
    setMonthTotals(null);
    getZktPunches(Number(month), Number(year), { employeeId })
      .then((data) => {
        if (cancelled) return;
        setPunchDays(Array.isArray(data?.days) ? data.days : []);
        setMonthTotals(data?.monthTotals ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setPunchDays([]);
        setMonthTotals(null);
        setError(getApiErrorMessage(err, "تعذر تحميل أيام البصمة لهذا الموظف"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId, month, year, reloadTick]);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const displayPunchDays = useMemo(
    () =>
      fillMonthPunchDays(punchDays, year, month, {
        id: employeeId,
        code: employeeCode,
        name: employeeName,
      }),
    [punchDays, year, month, employeeId, employeeCode, employeeName],
  );

  const viewerRole = user?.role;
  const canConvertPermit =
    isUuid(employeeId) &&
    (viewerRole === "admin" ||
      viewerRole === "gm" ||
      isHrFamilyRole(viewerRole));

  function canConvertDay(day) {
    if (!canConvertPermit || day?.empty) return false;
    return canHrConvertAttendanceToPermit(day, { role: viewerRole });
  }

  function canMovePermitDay(day) {
    if (!canConvertPermit || day?.empty) return false;
    return listPunchPermits(day?.permits).some((permit) => permit?.id);
  }

  const highlightIso = toIsoDate(highlightDate);
  const monthLabel =
    ARABIC_MONTHS.find((item) => item.value === Number(month))?.label || month;

  function handleShift(delta) {
    const next = shiftMonth(month, year, delta);
    setMonth(next.month);
    setYear(next.year);
  }

  return (
    <div className="epdm-overlay" onClick={onClose} role="presentation">
      <div
        className="epdm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="epdm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="epdm-header">
          <div>
            <h3 className="epdm-title" id="epdm-title">
              أيام البصمة — {employeeName || "الموظف"}
              {employeeCode ? ` (${employeeCode})` : ""}
            </h3>
            <p className="epdm-subtitle">
              كل أيام {monthLabel} {year} من اليوم 1 حتى آخر يوم
              {canConvertPermit
                ? " — يمكن تحويل التأخير/الانصراف المبكر إلى إذن، أو نقل إذن من يوم غلط ليوم صح"
                : ""}
            </p>
          </div>
          <button type="button" className="epdm-close" onClick={onClose}>
            إغلاق
          </button>
        </div>

        <div className="epdm-month-nav">
          <button type="button" className="epdm-month-btn" onClick={() => handleShift(-1)}>
            الشهر السابق
          </button>
          <span className="epdm-month-label">
            {monthLabel} {year}
          </span>
          <button type="button" className="epdm-month-btn" onClick={() => handleShift(1)}>
            الشهر التالي
          </button>
        </div>

        {error ? (
          <div className="epdm-alert epdm-alert-error" role="alert">
            {error}
          </div>
        ) : null}
        {convertSuccess ? (
          <div className="epdm-alert epdm-alert-success" role="status">
            {convertSuccess}
          </div>
        ) : null}
        {loading ? (
          <div className="epdm-loading">جاري تحميل أيام البصمة...</div>
        ) : null}
        {!loading && !error && displayPunchDays.length === 0 ? (
          <div className="epdm-alert">لا توجد أيام لهذا الشهر</div>
        ) : null}
        {!loading && !error && displayPunchDays.length > 0 ? (
          <>
            <div className="epdm-table-wrap">
              <table className="epdm-table">
                <thead>
                  <tr>
                    <th>التاريخ</th>
                    <th>مكان البصمة</th>
                    <th>البصمات</th>
                    <th>الدخول</th>
                    <th>خروج البريك</th>
                    <th>عودة البريك</th>
                    <th>الخروج</th>
                    <th>الوقت</th>
                    <th>التأخير</th>
                    <th>انصراف مبكر</th>
                    <th>الحالة</th>
                    <th>الإجازة</th>
                    <th>الإذن</th>
                    <th>المصدر</th>
                    {canConvertPermit ? <th>إجراء</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {displayPunchDays.map((day) => {
                    const { isHighlight, className } = punchDayRowState(day, highlightIso);
                    return (
                      <tr key={day.id || `${day.employeeId}-${day.date}`} className={className}>
                        <td>
                          {day.date}
                          {isHighlight ? (
                            <span className="epdm-incident-mark"> يوم الواقعة</span>
                          ) : null}
                          <TimesheetCompareUnderDay timesheet={day.timesheet} />
                        </td>
                        <td>
                          <PunchLocationLabel location={day.location} />
                        </td>
                        <td>
                          {day.actualPunchCount ?? "—"} / {day.expectedPunchCount ?? "—"}
                        </td>
                        <td className="punch-datetime">{formatAttendanceDateTimeCompact(day.checkInTime)}</td>
                        <td className="punch-datetime">{formatAttendanceDateTimeCompact(day.breakOutTime)}</td>
                        <td className="punch-datetime">{formatAttendanceDateTimeCompact(day.breakInTime)}</td>
                        <td className="punch-datetime">{formatAttendanceDateTimeCompact(day.checkOutTime)}</td>
                        <td>
                          {day.empty
                            ? "—"
                            : formatHoursAndMinutes(hoursToMinutes(day.totalWorkingHours))}
                        </td>
                        <td>
                          {day.empty ? (
                            "—"
                          ) : (
                            <span title={formatHoursAndMinutes(day.lateMinutes)}>
                              {formatHoursAndMinutes(day.lateMinutes)}
                            </span>
                          )}
                        </td>
                        <td>
                          {day.empty ? (
                            "—"
                          ) : (
                            <span title={formatHoursAndMinutes(day.earlyMinutes)}>
                              {formatHoursAndMinutes(day.earlyMinutes)}
                            </span>
                          )}
                        </td>
                        <td>{renderDayStatus(day)}</td>
                        <td>
                          <PunchLeaveCoverCell leaves={day.leaves} />
                        </td>
                        <td>
                          <PunchPermitCoverCell permits={day.permits} />
                        </td>
                        <td>{formatPunchSource(day.source)}</td>
                        {canConvertPermit ? (
                          <td>
                            <div className="epdm-actions">
                              {canConvertDay(day) ? (
                                <button
                                  type="button"
                                  className="epdm-convert-btn"
                                  onClick={() => setConvertDay(day)}
                                >
                                  تحويل لإذن
                                </button>
                              ) : null}
                              {canMovePermitDay(day) ? (
                                <button
                                  type="button"
                                  className="epdm-convert-btn"
                                  onClick={() => setMovePermitDay(day)}
                                >
                                  نقل الإذن
                                </button>
                              ) : null}
                              {!canConvertDay(day) && !canMovePermitDay(day)
                                ? "—"
                                : null}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PunchMonthTotals days={displayPunchDays} monthTotals={monthTotals} />
            <div className="epdm-cards">
              {displayPunchDays.map((day) => (
                <PunchDayCard
                  key={`card-${day.id || `${day.employeeId}-${day.date}`}`}
                  day={day}
                  highlightIso={highlightIso}
                  onConvertPermit={
                    canConvertDay(day) ? () => setConvertDay(day) : undefined
                  }
                  onMovePermit={
                    canMovePermitDay(day)
                      ? () => setMovePermitDay(day)
                      : undefined
                  }
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
      {convertDay ? (
        <ConvertAttendanceToPermitModal
          employeeId={employeeId}
          date={convertDay.date}
          employeeName={employeeName}
          onClose={() => setConvertDay(null)}
          onConverted={(result) => {
            const hours = (result?.permits || []).reduce(
              (sum, permit) => sum + Number(permit.hours || 0),
              0,
            );
            setConvertSuccess(
              result?.partial
                ? `تم التحويل في حدود الرصيد (${hours} ساعة)`
                : `تم تحويل اليوم إلى إذن (${hours} ساعة)`,
            );
            setConvertDay(null);
            setReloadTick((tick) => tick + 1);
          }}
        />
      ) : null}
      {movePermitDay ? (
        <MovePermitDateModal
          employeeName={employeeName}
          fromDate={movePermitDay.date}
          permits={movePermitDay.permits}
          onClose={() => setMovePermitDay(null)}
          onMoved={(_result, meta) => {
            setConvertSuccess(
              `تم نقل الإذن من ${meta.fromDate} إلى ${meta.newDate}`,
            );
            setMovePermitDay(null);
            setReloadTick((tick) => tick + 1);
          }}
        />
      ) : null}
    </div>
  );
}
